import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Box, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, useTheme, useMediaQuery,
  Card, CardContent, Divider, Chip,
  Grid
} from '@mui/material';
import { Edit, Delete, Add, Save, BarChart, Close, Link as LinkIcon, Refresh, NotificationsActive } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' }
  }
};

const NotificationsAdmin = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [items, setItems] = useState([]);
  const [subscriptionCount, setSubscriptionCount] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLogs, setReportLogs] = useState([]);
  const [reportingNotification, setReportingNotification] = useState(null);

  useEffect(() => { loadItems(); }, []);

  const getId = (it) => it?._id || it?.id || '';

  const loadItems = async () => {
    try {
      const res = await api.get('/admin/notifications');
      const data = res?.data || [];
      setItems(Array.isArray(data) ? data : []);
      loadSubscriptionCount();
    } catch (e) {
      console.error(e);
      snackbar('خطا در بارگذاری اعلان‌ها');
      setItems([]);
    }
  };

  const loadSubscriptionCount = async () => {
    try {
      const res = await api.get('/admin/notifications/subscriptions/count');
       setSubscriptionCount(res?.data?.count ?? 0);
    } catch (e) {
      console.error(e);
      setSubscriptionCount(null);
    }
  };

  const openReport = async (it) => {
    const id = getId(it);
    try {
      const res = await api.get(`/admin/notifications/${id}/delivery`);
      setReportLogs(res?.data || []);
      setReportingNotification(it);
      setReportOpen(true);
    } catch (e) {
      console.error(e);
      snackbar('خطا در بارگذاری گزارش ارسال');
    }
  };

  const closeReport = () => { setReportOpen(false); setReportLogs([]); setReportingNotification(null); };

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ title: '', text: '', link: '', linkText: '' });

  const handleOpen = (it = null) => {
    if (it) {
      setCurrent(it);
      setForm({ title: it.title || '', text: it.text || '', link: it.link || '', linkText: it.linkText || '' });
    } else {
      setCurrent(null);
      setForm({ title: '', text: '', link: '', linkText: '' });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!form.title || !form.text) return snackbar('عنوان و متن لازم است');
    try {
      if (current) {
        const id = getId(current);
        const payload = { title: form.title, text: form.text, link: form.link, linkText: form.linkText };
        const res = await api.put(`/admin/notifications/${id}`, payload);
        const updated = res?.data || { ...current, ...payload, updatedAt: new Date().toISOString() };
        setItems((prev) => prev.map((it) => getId(it) === id ? updated : it));
        successMessage('اعلان بروزرسانی شد');
      } else {
        const payload = { title: form.title, text: form.text, link: form.link, linkText: form.linkText };
        const res = await api.post('/admin/notifications', payload);
        const created = res?.data || { ...payload, createdAt: new Date().toISOString() };
        setItems((prev) => [created, ...prev]);
        successMessage('اعلان افزوده شد');
      }
      handleClose();
    } catch (err) {
      console.error(err);
      snackbar('خطا در ذخیره اعلان');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این اعلان اطمینان دارید؟')) return;
    try {
      await api.delete(`/admin/notifications/${id}`);
      setItems((prev) => prev.filter((it) => getId(it) !== id));
      successMessage('حذف شد');
    } catch (err) {
      console.error(err);
      snackbar('خطا در حذف اعلان');
    }
  };



  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'} 
        justifyContent="space-between" 
        alignItems={isMobile ? 'stretch' : 'center'} 
        gap={2}
        mb={3}
      >
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">مدیریت اعلان‌های همگانی</Typography>
        <Box display="flex" gap={1}>
           <IconButton 
            onClick={loadItems} 
            sx={{ bgcolor: 'grey.100' }}
          >
            <Refresh fontSize="small" />
          </IconButton>
          <Chip 
            icon={<NotificationsActive fontSize="small" />} 
            label={`اشتراک‌ها: ${subscriptionCount ?? '-'}`} 
            variant="outlined"
            onClick={loadSubscriptionCount}
            sx={{ borderRadius: 2, height: '40px' }}
          />
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => handleOpen()}
            fullWidth={isMobile}
            sx={{ borderRadius: 3, px: 3 }}
          >
            اعلان جدید
          </Button>
        </Box>
      </Box>

      {isMobile ? (
        <Grid container spacing={2}>
          {items.map((it) => (
            <Grid item xs={12} key={getId(it)}>
               <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {it.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {it.createdAt ? new Date(it.createdAt).toLocaleDateString('fa-IR') : '-'}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2} sx={{ lineHeight: 1.6 }}>
                        {it.text}
                    </Typography>
                    
                    {it.link && (
                      <Box display="flex" alignItems="center" gap={0.5} mb={2}>
                        <LinkIcon fontSize="inherit" color="primary" />
                        <Typography variant="caption" color="primary" component="a" href={it.link} target="_blank" rel="noreferrer" sx={{ textDecoration: 'none' }}>
                            {it.linkText || 'مشاهده لینک'}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />
                    
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <IconButton 
                        size="small" 
                        sx={{ bgcolor: 'info.lighter', color: 'info.main' }}
                        onClick={() => openReport(it)}
                      >
                        <BarChart fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ bgcolor: 'primary.lighter', color: 'primary.main' }}
                        onClick={() => handleOpen(it)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ bgcolor: 'error.lighter', color: 'error.main' }}
                        onClick={() => handleDelete(getId(it))}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
               </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>عنوان و تاریخ</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>متن اعلان</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>لینک همراه</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={getId(it)} hover>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">{it.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {it.createdAt ? new Date(it.createdAt).toLocaleString('fa-IR') : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ maxWidth: 400 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{it.text}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {it.link ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                             <LinkIcon fontSize="small" color="disabled" />
                             <a href={it.link} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: theme.palette.primary.main }}>{it.linkText || 'مشاهده'}</a>
                        </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                        <IconButton color="info" size="small" onClick={() => openReport(it)} title="گزارش تحویل"><BarChart fontSize="small" /></IconButton>
                        <IconButton color="primary" size="small" onClick={() => handleOpen(it)}><Edit fontSize="small" /></IconButton>
                        <IconButton color="error" size="small" onClick={() => handleDelete(getId(it))}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">{current ? 'ویرایش اعلان' : 'ایجاد اعلان جدید'}</Typography>
            {isMobile && <IconButton onClick={handleClose} size="small"><Close /></IconButton>}
        </DialogTitle>
        <DialogContent dividers>
          <Box pt={1}>
            <TextField fullWidth name="title" label="عنوان اعلان" value={form.title} onChange={handleChange} sx={textFieldStyle} />
            <TextField fullWidth name="text" label="متن پیام (Push Notification Body)" value={form.text} onChange={handleChange} multiline minRows={4} sx={textFieldStyle} />
            <TextField fullWidth name="link" label="لینک مقصد (URL)" value={form.link} onChange={handleChange} sx={textFieldStyle} placeholder="https://..." />
            <TextField fullWidth name="linkText" label="عنوان دکمه لینک" value={form.linkText} onChange={handleChange} sx={textFieldStyle} placeholder="مثلا: مشاهده جزئیات" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button fullWidth={isMobile} onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>انصراف</Button>
          <Button fullWidth={isMobile} variant="contained" startIcon={<Save />} onClick={handleSubmit} sx={{ borderRadius: 2 }}>
            {current ? 'بروزرسانی و ارسال' : 'ایجاد و ارسال همگانی'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={reportOpen} 
        onClose={closeReport} 
        fullWidth 
        maxWidth="lg"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">گزارش ارسال: {reportingNotification?.title}</Typography>
            <IconButton onClick={closeReport} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isMobile ? (
             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {reportLogs.map((r, i) => (
                    <Card key={i} variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Chip 
                                    label={r.success ? 'موفق' : 'ناموفق'} 
                                    size="small" 
                                    color={r.success ? 'success' : 'error'} 
                                />
                                <Typography variant="caption" color="text.secondary">
                                    تلاش: {r.attempts || 1}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ wordBreak: 'break-all', display: 'block', mb: 1, p: 0.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                {r.endpoint}
                            </Typography>
                            {r.error && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                    خطا: {r.error}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                ))}
             </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, borderRadius: 3 }}>
                <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell align="center">وضعیت</TableCell>
                    <TableCell align="center">کد</TableCell>
                    <TableCell>جزئیات خطا</TableCell>
                    <TableCell align="center">تعداد تلاش</TableCell>
                    <TableCell align="right">زمان</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {reportLogs.map((r, i) => (
                    <TableRow key={i}>
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.endpoint}</TableCell>
                        <TableCell align="center">
                            <Chip size="small" label={r.success ? 'موفق' : 'خطا'} color={r.success ? 'success' : 'error'} variant="outlined" />
                        </TableCell>
                        <TableCell align="center">{r.statusCode || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', fontSize: '11px' }}>{r.error || '-'}</TableCell>
                        <TableCell align="center">{r.attempts ?? '-'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '11px' }}>{r.createdAt ? new Date(r.createdAt).toLocaleString('fa-IR') : '-'}</TableCell>
                    </TableRow>
                    ))}
                </TableBody>
                </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button fullWidth={isMobile} onClick={closeReport} variant="contained" sx={{ borderRadius: 2 }}>بستن گزارش</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NotificationsAdmin;
 
